/**
 * The portfolio's working day, as the scheduling engine sees it.
 *
 * The day starts at 09:00 and ends at 09:00 + the portfolio's Daily FTE
 * (7.8 h -> 16:48). That length is what Bryntum values a "day" of effort
 * at, so it MUST match the backend, which computes every workload from
 * `Portfolio.dailyFTE` (AllocationUtils.convertAllocationToUnits,
 * WorkAssignmentCalculator.computeAssignedWork). The previous hard-coded
 * 09:00-16:00 valued a day at 7 h and made FixedUnits durations wrong on
 * every portfolio whose Daily FTE was not 7.
 *
 * Deliberately separate from displayUnits.js: that module converts values
 * for *display* and must never change scheduling geometry. This one is the
 * geometry.
 *
 * Same cache pattern as displayUnits: written by portfolioActions on
 * getDetails/updateDetails success, ahead of any component consuming the
 * details, and read lazily so pre-fetch renders fall back to the legacy 7 h.
 */

const DAY_START_HOUR = 9;
const DEFAULT_WORKING_DAY_HOURS = 7;

/**
 * Milestones are pinned to 00:01 and 23:59:59 (formatOneTask in
 * Bryntum/Gantt/utils.js) and rely on those hours being NON-working. A day
 * long enough to reach them would silently break that pinning, so the length
 * is capped at 14 h — 09:00 + 14 h = 23:00. Daily FTE itself is only bounded
 * 4..20 (SettingsPage validation), hence the guard here.
 */
const MAX_WORKING_DAY_HOURS = 14;

const cache = { workingDayHours: DEFAULT_WORKING_DAY_HOURS };

/** Feed the cache from the portfolio details payload (`state.portfolio.details`). */
export const setSchedulingCalendarSettings = (details) => {
	const hours = Number(details?.dailyFTE);
	cache.workingDayHours =
		Number.isFinite(hours) && hours > 0
			? Math.min(hours, MAX_WORKING_DAY_HOURS)
			: DEFAULT_WORKING_DAY_HOURS;
};

export const workingDayHours = () => cache.workingDayHours;

export const workingDayStartHour = () => DAY_START_HOUR;

/** End of the working day as `{ hour, minute }` — 7.8 h yields 16:48. */
export const workingDayEnd = () => {
	const totalMinutes = Math.round(
		DAY_START_HOUR * 60 + cache.workingDayHours * 60
	);
	return { hour: Math.floor(totalMinutes / 60), minute: totalMinutes % 60 };
};

/**
 * Stamp the end of the working day onto a date. Replaces date-fns
 * `setHours(date, 16)` at call sites that anchor a finish date: an
 * integer-hour setter cannot express the 48 minutes of a 7.8 h day.
 */
export const applyWorkingDayEnd = (date) => {
	const { hour, minute } = workingDayEnd();
	const stamped = new Date(date);
	stamped.setHours(hour, minute, 0, 0);
	return stamped;
};

export const _resetSchedulingCalendarSettingsForTests = () => {
	cache.workingDayHours = DEFAULT_WORKING_DAY_HOURS;
};
